import { useEffect, useState } from 'react';
import supabase from '../services/supabase';
import { useAuth } from '../components/auth/AuthContext';
import { getStaffPositionTypes } from '../services/positions';

/**
 * Count of currently-flagged case notes that match the user's role:
 *   - team_leader      → notes with flagged_for_team_leaders = true
 *   - department_leader → notes with flagged_for_department_leader = true
 *   - both             → notes with either flag set (what the Flagged page shows)
 *
 * Returns 0 for users without either position, so the nav badge only appears
 * for people expected to action them.
 *
 * Re-fetches on mount and whenever `refreshKey` changes (pass location.pathname
 * so the badge refreshes on navigation — e.g. right after an Unflag).
 */
export function useFlaggedNotesCount(refreshKey?: unknown): number {
  const { user } = useAuth();
  const [count, setCount] = useState<number>(0);

  useEffect(() => {
    if (!user?.uid) {
      setCount(0);
      return;
    }

    let cancelled = false;

    const fetchCount = async () => {
      try {
        const positions = await getStaffPositionTypes(user.uid);
        const isTeamLeader = positions.includes('team_leader');
        const isDepartmentLeader = positions.includes('department_leader');
        if (!isTeamLeader && !isDepartmentLeader) {
          if (!cancelled) setCount(0);
          return;
        }

        // Count currently-flagged case notes visible to the user's role:
        //   Department leader (alone or with team lead) → sees ALL flagged notes
        //   Team leader only                             → only team-flagged notes
        let query = supabase
          .from('case_notes')
          .select('id', { count: 'exact', head: true });

        if (isDepartmentLeader) {
          query = query.or(
            'flagged_for_team_leaders.eq.true,flagged_for_department_leader.eq.true'
          );
        } else {
          // team_leader only
          query = query.eq('flagged_for_team_leaders', true);
        }

        const { count: c, error } = await query;

        if (!cancelled && !error) {
          setCount(c ?? 0);
        }
      } catch {
        if (!cancelled) setCount(0);
      }
    };

    fetchCount();

    // Poll while mounted so the badge stays reasonably fresh.
    const interval = setInterval(fetchCount, 60_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [user?.uid, refreshKey]);

  return count;
}
